import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { platformCoreNetworkApi } from '../../../api/platform/coreNetwork';
import { useAsyncData } from '../hooks/useAsyncData';
import { DataTable, EmptyState, ErrorState, FieldGroup, FilterGrid, LoadingState, MvpCard, MvpTalentLayout, PaginationControls, StatusBadge } from '../components/MvpTalentUi';
import { formatDateTime } from '../utils/format';

const WorkListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => ({
    page: Number(searchParams.get('page') || 1),
    limit: 10,
    instanceId: searchParams.get('instanceId') || '',
    status: searchParams.get('status') || '',
    assignmentStatus: searchParams.get('assignmentStatus') || '',
    requestType: searchParams.get('requestType') || '',
    search: searchParams.get('search') || '',
    overdue: searchParams.get('overdue') || '',
  }), [searchParams]);
  const { data, loading, error, reload } = useAsyncData((signal) => platformCoreNetworkApi.listWorkRequests(filters, { signal }), [filters.page, filters.instanceId, filters.status, filters.assignmentStatus, filters.requestType, filters.search, filters.overdue]);

  const updateFilter = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value); else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  return (
    <MvpTalentLayout title="Work Oversight" subtitle="Cross-instance view of Core Network requests, assignment state, and support queues.">
      <MvpCard title="Filters">
        <FilterGrid>
          <FieldGroup label="Search"><input value={filters.search} onChange={(event) => updateFilter('search', event.target.value)} placeholder="Request, order, description" /></FieldGroup>
          <FieldGroup label="Instance ID"><input value={filters.instanceId} onChange={(event) => updateFilter('instanceId', event.target.value)} /></FieldGroup>
          <FieldGroup label="Status"><input value={filters.status} onChange={(event) => updateFilter('status', event.target.value)} placeholder="submitted" /></FieldGroup>
          <FieldGroup label="Assignment status"><input value={filters.assignmentStatus} onChange={(event) => updateFilter('assignmentStatus', event.target.value)} placeholder="accepted" /></FieldGroup>
          <FieldGroup label="Request type"><input value={filters.requestType} onChange={(event) => updateFilter('requestType', event.target.value)} placeholder="artwork-proof" /></FieldGroup>
          <FieldGroup label="Overdue">
            <select value={filters.overdue} onChange={(event) => updateFilter('overdue', event.target.value)}>
              <option value="">All</option>
              <option value="true">Overdue only</option>
            </select>
          </FieldGroup>
        </FilterGrid>
      </MvpCard>
      {loading ? <LoadingState label="Loading work requests..." /> : null}
      {error ? <ErrorState message={error} onRetry={reload} /> : null}
      {data ? (
        <MvpCard title="Work requests">
          {data.rows.length ? (
            <>
              <DataTable columns={['Request', 'Instance', 'Type', 'Talent', 'Status', 'Assignment', 'Due', 'Last activity', '']}>
                {data.rows.map((request) => (
                  <tr key={request.id}>
                    <td>{request.title || `Request #${request.id}`}</td>
                    <td>{request.instanceName || request.instanceId}</td>
                    <td>{request.requestType}</td>
                    <td>{request.assignment?.member?.displayName || 'Unassigned'}</td>
                    <td><StatusBadge status={request.status} /></td>
                    <td><StatusBadge status={request.assignment?.status || 'unassigned'} /></td>
                    <td>{formatDateTime(request.deadlineAt)}</td>
                    <td>{formatDateTime(request.lastActivityAt)}</td>
                    <td><Link to={`/command-center/mvp-talent/work/${request.id}`}>Open</Link></td>
                  </tr>
                ))}
              </DataTable>
              <PaginationControls page={data.pagination.page} totalPages={data.pagination.pages} onPageChange={(nextPage) => updateFilter('page', String(nextPage))} />
            </>
          ) : <EmptyState title="No work requests" message="No work requests matched the current filters." />}
        </MvpCard>
      ) : null}
    </MvpTalentLayout>
  );
};

export default WorkListPage;
